import React from 'react';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';
import { StudentTopicActivity } from '../../../services/tutorRosterService';
import { TopicLifecycleDTO } from '../../../services/tutorLifecycleService';

interface Props {
  activities: StudentTopicActivity[];
  courseId: number;
  lifecycles: Map<number, TopicLifecycleDTO>;
  revisionFlags: Map<number, boolean>;
  onMarkStarted: (nodeId: number) => Promise<void>;
  onMarkCompleted: (nodeId: number) => Promise<void>;
  onToggleRevision: (nodeId: number, currentlyFlagged: boolean) => Promise<void>;
  actionLoading: number | null;
}

function activityStatusColor(status: string): 'success' | 'warning' | 'default' {
  if (status === 'COMPLETED') return 'success';
  if (status === 'IN_PROGRESS') return 'warning';
  return 'default';
}

function lifecycleColor(state: string): 'success' | 'primary' | 'info' | 'default' {
  if (state === 'COMPLETED') return 'success';
  if (state === 'STARTED') return 'primary';
  if (state === 'SCHEDULED') return 'info';
  return 'default';
}

export default function StudentTopicActivityPanel({
  activities,
  lifecycles,
  revisionFlags,
  onMarkStarted,
  onMarkCompleted,
  onToggleRevision,
  actionLoading,
}: Props) {
  if (activities.length === 0) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography variant="body2" color="text.secondary">No topics in this course.</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ overflowX: 'auto' }}>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Topic</TableCell>
            <TableCell align="center">Accessed</TableCell>
            <TableCell>Progress</TableCell>
            <TableCell align="center">Quiz</TableCell>
            <TableCell align="right">Score</TableCell>
            <TableCell>Teaching</TableCell>
            <TableCell>Actions</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {activities.map((a) => {
            const lc = lifecycles.get(a.nodeId);
            const lcState = lc?.state ?? 'NONE';
            const isRevision = revisionFlags.get(a.nodeId) ?? false;
            const loading = actionLoading === a.nodeId;

            return (
              <TableRow key={a.nodeId}>
                <TableCell>
                  <Box>
                    {a.topicTitle}
                    {isRevision && (
                      <Chip label="Revision" size="small" color="warning" sx={{ ml: 0.5 }} />
                    )}
                  </Box>
                </TableCell>
                <TableCell align="center">
                  {a.contentAccessed
                    ? <CheckCircleOutlineIcon color="success" fontSize="small" />
                    : <CancelOutlinedIcon color="disabled" fontSize="small" />}
                </TableCell>
                <TableCell>
                  <Chip
                    label={a.topicStatus.replace('_', ' ')}
                    size="small"
                    color={activityStatusColor(a.topicStatus)}
                  />
                </TableCell>
                <TableCell align="center">{a.quizAttemptCount}</TableCell>
                <TableCell align="right">
                  {a.latestQuizScore != null ? `${a.latestQuizScore}%` : '—'}
                </TableCell>
                <TableCell>
                  {lcState !== 'NONE' && (
                    <Chip
                      label={lcState}
                      size="small"
                      color={lifecycleColor(lcState)}
                      variant="outlined"
                    />
                  )}
                </TableCell>
                <TableCell>
                  {loading ? (
                    <CircularProgress size={16} />
                  ) : (
                    <Stack direction="row" spacing={0.5}>
                      {lcState === 'NONE' || lcState === 'SCHEDULED' ? (
                        <Button size="small" variant="outlined" onClick={() => onMarkStarted(a.nodeId)}>
                          Start
                        </Button>
                      ) : null}
                      {lcState === 'STARTED' ? (
                        <Button size="small" variant="contained" color="success" onClick={() => onMarkCompleted(a.nodeId)}>
                          Complete
                        </Button>
                      ) : null}
                      <Button
                        size="small"
                        variant={isRevision ? 'contained' : 'outlined'}
                        color={isRevision ? 'warning' : 'inherit'}
                        onClick={() => onToggleRevision(a.nodeId, isRevision)}
                        sx={{ minWidth: 0, px: 1 }}
                      >
                        {isRevision ? 'Unrev' : 'Rev'}
                      </Button>
                    </Stack>
                  )}
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </Box>
  );
}
